import type { FirebaseRepository } from './FirebaseRepository'
import type { MobxDocument } from '../types'
import {
  ObservableModelWithDecorators,
  emptyCollection,
  emptyModel,
} from '../firestore-mobx/model'
import {
  SlideDeckMaterialType,
  SlideDeckMaterialWorkType,
  empty,
  type FirestoreSlideDeckMaterial,
} from '../firestore/SlideDeckMaterial/schema'

export class SlideDeckMaterial extends ObservableModelWithDecorators<FirestoreSlideDeckMaterial> {
  static empty(repository: FirebaseRepository) {
    return emptyModel(repository, this, empty)
  }

  static emptyCollection(repository: FirebaseRepository) {
    return emptyCollection(repository, this, empty)
  }

  constructor(
    repository: FirebaseRepository,
    doc: MobxDocument<FirestoreSlideDeckMaterial>
  ) {
    super(repository, doc)
  }

  get isPreWork() {
    return this.data.materialWorkType === SlideDeckMaterialWorkType.preWork
  }

  get isPostWork() {
    return this.data.materialWorkType === SlideDeckMaterialWorkType.postWork
  }

  get isPodcast() {
    return this.data.materialType === SlideDeckMaterialType.mp3
  }

  get isPDF() {
    return this.data.materialType === SlideDeckMaterialType.pdf
  }

  /// podcasts have imageURL field, as of now this is the only material type with
  /// two file urls/file types
  get uploadFieldsWithExtensions() {
    return slideDeckMaterialUploadFieldsWithExtensionsFromMaterialType(
      this.data.materialType
    )
  }

  /**
   * Check if the materialLink is a signed URL that has expired.
   * Returns false for non-signed URLs (e.g. direct Firestore storage paths).
   */
  get isSignedUrlExpired() {
    const expiresAt = this.signedUrlExpiresAt
    return expiresAt !== null && Date.now() > expiresAt
  }

  /**
   * When the materialLink's signature expires, in epoch milliseconds.
   * Parses X-Goog-Date and X-Goog-Expires from the query params.
   * Null for non-signed URLs (e.g. direct Firestore storage paths).
   */
  get signedUrlExpiresAt(): number | null {
    const { materialLink } = this.data
    if (!materialLink || !materialLink.includes('X-Goog-Signature='))
      return null

    try {
      const url = new URL(materialLink)
      const googDate = url.searchParams.get('X-Goog-Date')
      const googExpires = url.searchParams.get('X-Goog-Expires')
      if (!googDate || !googExpires) return null

      // X-Goog-Date format: 20260531T210045Z
      const year = googDate.slice(0, 4)
      const month = googDate.slice(4, 6)
      const day = googDate.slice(6, 8)
      const hour = googDate.slice(9, 11)
      const minute = googDate.slice(11, 13)
      const second = googDate.slice(13, 15)
      const signedAt = new Date(
        `${year}-${month}-${day}T${hour}:${minute}:${second}Z`
      )
      return signedAt.getTime() + Number(googExpires) * 1000
    } catch {
      return null
    }
  }

  get currentMaterialLinkExtension() {
    // get the current extension for the material link if present
    // ex: https://example.com/v0/b/bucket/o/some_path_name.jpg?alt=media
    // -> jpg
    const { materialLink } = this.data
    if (!materialLink) return null

    const url = new URL(materialLink)
    const pathname = url.pathname
    const parts = pathname.split('.')
    if (parts.length < 2) return null
    return parts[parts.length - 1].toLowerCase()
  }

  safeFileName(extension: string) {
    const { materialLink: validExtensions } = this.uploadFieldsWithExtensions

    const isValidExtension = validExtensions?.some(
      ({ extension: allowedExtension }) => allowedExtension === extension
    )

    if (!isValidExtension)
      throw new Error(
        `Invalid file extension ${extension} for material type ${this.data.materialType}`
      )

    if (this.data.materialMediaFilename)
      return `${this.data.materialMediaFilename}.${extension}`

    // if no defined file name, make one
    const safeName = this.data.materialName
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^_+|_+$/g, '')
      .substring(0, 255)

    return `${safeName}.${extension}`
  }
}

type FileExtensionWithMimeType = {
  readonly extension: string
  readonly mimeType: string
}

type UploadFieldMapping = {
  readonly materialLink: readonly FileExtensionWithMimeType[]
  readonly imageUrl?: readonly FileExtensionWithMimeType[]
}

export const slideDeckMaterialUploadFieldsWithExtensionsFromMaterialType = (
  materialType: SlideDeckMaterialType
): UploadFieldMapping => {
  const mp3 = {
    extension: 'mp3',
    mimeType: 'audio/mpeg',
  } as const
  const mp4 = {
    extension: 'mp4',
    mimeType: 'video/mp4',
  } as const
  const jpg = {
    extension: 'jpg',
    mimeType: 'image/jpeg',
  } as const
  const jpeg = {
    extension: 'jpeg',
    mimeType: 'image/jpeg',
  } as const
  const png = {
    extension: 'png',
    mimeType: 'image/png',
  } as const
  const webm = {
    extension: 'webm',
    mimeType: 'video/webm',
  } as const
  const pdf = {
    extension: 'pdf',
    mimeType: 'application/pdf',
  } as const

  // record with every key of SlideDeckMaterialType
  const uploadableTypesForFieldByType: Record<
    SlideDeckMaterialType,
    UploadFieldMapping
  > = {
    [SlideDeckMaterialType.mp3]: {
      materialLink: [mp3],
      imageUrl: [jpg, jpeg, png],
    },
    [SlideDeckMaterialType.pdf]: {
      materialLink: [pdf],
    },
    [SlideDeckMaterialType.webm]: {
      materialLink: [webm],
    },
    [SlideDeckMaterialType.mp4]: {
      materialLink: [mp4],
    },
    [SlideDeckMaterialType.featuredLarge]: {
      materialLink: [jpg, jpeg, png],
    },
    [SlideDeckMaterialType.img]: {
      materialLink: [jpg, jpeg, png],
    },
    [SlideDeckMaterialType.featuredSquare]: {
      materialLink: [jpg, jpeg, png],
    },
    [SlideDeckMaterialType.url]: {
      materialLink: [],
    },
  }

  return uploadableTypesForFieldByType[materialType]
}
